import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { billingApi } from "../../shared/api/billing";
import {
  appointmentQueryKeys,
  appointmentsApi,
} from "../../shared/api/appointments";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import type {
  BillingPatientOption,
  DiscountType,
  InvoiceItemInput,
} from "../../shared/types/billing";
import { InvoicePatientPicker } from "./components/InvoicePatientPicker";
import { InvoiceItemsEditor } from "./components/InvoiceItemsEditor";
import { InvoiceTotals } from "./components/InvoiceTotals";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

// Same native-select look as the other doctor pickers (AppointmentDetailPage).
const NATIVE_CONTROL =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

export function InvoiceCreatePage() {
  const navigate = useNavigate();
  const [patient, setPatient] = useState<BillingPatientOption | null>(null);
  // #19223 — the doctor is stored by id (invoices.doctor_profile_id, as the visit-generated
  // invoice does since #19169). A free-text name had nowhere to go and was silently dropped.
  const [doctorId, setDoctorId] = useState<string | null>(null);
  const doctorOptions = useQuery({
    queryKey: appointmentQueryKeys.doctors(),
    queryFn: () => appointmentsApi.listDoctors(),
    staleTime: 5 * 60 * 1000,
  });
  const [items, setItems] = useState<InvoiceItemInput[]>([]);
  const [discountType, setDiscountType] = useState<DiscountType | null>(null);
  const [discountValue, setDiscountValue] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const create = useMutation({
    // Invoices are issued (POSTED) on create — there is no DRAFT state — so there is a
    // single action, not a draft/post split.
    mutationFn: async () => {
      const detail = await billingApi.create({
        patientId: patient!.id,
        encounterId: null,
        doctorProfileId: doctorId,
        items,
        discountType,
        discountValue,
        note: note || null,
      });
      return billingApi.post(detail.id);
    },
    onSuccess: (res) => navigate(`/invoices/${res.id}`),
    onError: (e) => setErrors({ _form: apiError(e).detail }),
  });

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!patient) e.patient = "Vui lòng chọn bệnh nhân";
    if (items.length === 0) e.items = "Cần ít nhất 1 mục";
    for (const it of items) {
      if (it.kind === "CUSTOM" && !it.name.trim()) {
        e.items = "Dòng tự nhập phải có tên";
        break;
      }
      if (it.unitPrice <= 0) {
        e.items = "Đơn giá phải lớn hơn 0";
        break;
      }
    }
    return e;
  }

  function onSubmit() {
    // Defence in depth (#18681), same as the booking form: the button is disabled while a
    // submit is in flight, but nothing stops a second call arriving another way. Not a fix for
    // anything observed here — a guard so it stays that way.
    if (create.isPending) return;
    const err = validate();
    setErrors(err);
    if (Object.keys(err).length > 0) return;
    create.mutate();
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <PageHeader
        title="Tạo hoá đơn mới"
        description="Tạo hoá đơn trực tiếp hoặc từ phiếu khám."
        breadcrumbs={[
          { label: "Hoá đơn", to: "/invoices" },
          { label: "Tạo mới" },
        ]}
        actions={
          <>
            <Button asChild variant="ghost">
              <Link to="/invoices">Hủy</Link>
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Đang lưu…" : "Ghi nhận hoá đơn"}
            </Button>
          </>
        }
      />

      {errors._form && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {errors._form}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Bệnh nhân</CardTitle>
          </CardHeader>
          <CardContent>
            <InvoicePatientPicker
              value={patient}
              onChange={setPatient}
              error={errors.patient}
              autoFocus
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Bác sĩ phụ trách</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            <select
              aria-label="Bác sĩ phụ trách"
              className={NATIVE_CONTROL}
              value={doctorId ?? ""}
              onChange={(e) => setDoctorId(e.target.value || null)}
            >
              <option value="">— Không chọn —</option>
              {(doctorOptions.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.specialty ? ` · ${d.specialty}` : ""}
                </option>
              ))}
            </select>
            <p className="text-sm text-muted-foreground">
              Tự động điền nếu tạo từ phiếu khám.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base">Mục thanh toán</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {errors.items && (
            <p className="text-sm text-destructive">{errors.items}</p>
          )}
          <InvoiceItemsEditor items={items} onChange={setItems} />
        </CardContent>
      </Card>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Ghi chú</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={note}
              placeholder="Ghi chú in trên cuối hoá đơn (tuỳ chọn)…"
              rows={5}
              onChange={(e) => setNote(e.target.value)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tổng kết</CardTitle>
          </CardHeader>
          <CardContent>
            <InvoiceTotals
              items={items}
              discountType={discountType}
              discountValue={discountValue}
              onDiscountChange={(t, v) => {
                setDiscountType(t);
                setDiscountValue(v);
              }}
            />
          </CardContent>
        </Card>
      </div>
    </form>
  );
}
